import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDownToLine, ArrowUpToLine, Copy, Loader2, MailPlus, Power, PowerOff, Trash2, UserPlus, Users } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, errorMessage } from "@/lib/api";
import { useMeta } from "@/hooks/useMeta";
import { useAuth } from "@/lib/auth";
import { fmtDateTime, fmtRelative } from "@/lib/format";
import { StatusBadge } from "@/components/dashboard/StatusBadge";
import { Btn, LabeledSelect, PageHeader } from "@/components/dashboard/Ui";

export default function UsersPage() {
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "admin" });
  const [inviteResult, setInviteResult] = useState(null);
  const [confirm, setConfirm] = useState(null); // {type, user}
  const [blocked, setBlocked] = useState(null); // {message, appointments}
  const { data: meta } = useMeta();
  const specialties = meta?.specialties || {};
  const specColor = (key) => specialties[key]?.color || null;

  const { data, isLoading } = useQuery({ queryKey: ["users"], queryFn: async () => (await api.get("/users")).data.items });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["users"] });
    qc.invalidateQueries({ queryKey: ["directory"] });
  };

  const handleError = (e) => {
    if (e?.response?.status === 409 && e.response.data?.detail?.appointments) {
      setBlocked(e.response.data.detail);
    } else toast.error(errorMessage(e));
  };

  const invite = useMutation({
    mutationFn: async () => (await api.post("/users/invite", form)).data,
    onSuccess: (res) => {
      toast.success(`Invitation created for ${form.name}`);
      setInviteResult(res);
      setForm({ name: "", email: "", role: "admin" });
      invalidate();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const setSpecialty = useMutation({
    mutationFn: async ({ user, specialty }) =>
      (await api.patch(`/users/${user.id}/specialty`, { specialty: specialty || null })).data,
    onSuccess: () => {
      toast.success("Specialty updated");
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["directory"] });
      qc.invalidateQueries({ queryKey: ["availability-overview"] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const action = useMutation({
    mutationFn: async ({ type, user, role }) => {
      if (type === "deactivate") return (await api.post(`/users/${user.id}/deactivate`)).data;
      if (type === "activate") return (await api.post(`/users/${user.id}/activate`)).data;
      if (type === "remove") return (await api.delete(`/users/${user.id}`)).data;
      if (type === "role") return (await api.patch(`/users/${user.id}/role`, { role })).data;
      if (type === "resend") return (await api.post(`/users/${user.id}/resend-invite`)).data;
      return null;
    },
    onSuccess: (res, vars) => {
      const msgs = { deactivate: "User deactivated", activate: "User reactivated", remove: "User removed", role: "Role updated", resend: "Invitation re-sent" };
      toast.success(msgs[vars.type]);
      if (vars.type === "resend") setInviteResult(res);
      setConfirm(null);
      invalidate();
    },
    onError: (e) => {
      setConfirm(null);
      handleError(e);
    },
  });

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Could not copy");
    }
  };
  const origin = window.location.origin;

  return (
    <div data-testid="users-page">
      <PageHeader
        title="Team"
        description="Invite admins, manage roles and access. Users with upcoming appointments must be reassigned before removal."
        actions={
          <Btn onClick={() => setInviteOpen(true)} data-testid="users-invite-button">
            <UserPlus /> Invite team member
          </Btn>
        }
      />

      <div className="emerge-card overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[980px] text-sm" data-testid="users-table">
              <thead className="bg-secondary/70 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 text-start font-semibold">Name</th>
                  <th className="px-4 py-3 text-start font-semibold">Email / username</th>
                  <th className="px-4 py-3 text-start font-semibold">Specialty</th>
                  <th className="px-4 py-3 text-start font-semibold">Role</th>
                  <th className="px-4 py-3 text-start font-semibold">Status</th>
                  <th className="px-4 py-3 text-start font-semibold">Last login</th>
                  <th className="px-4 py-3 text-start font-semibold">Upcoming</th>
                  <th className="px-4 py-3 text-end font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(data || []).map((u) => {
                  const self = u.id === me.id;
                  return (
                    <tr key={u.id} className="border-t border-border table-row-hover" data-testid="users-row">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <span className="flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold text-white" style={{ backgroundColor: u.color || "#2f5d83" }}>
                            {(u.name || "?").split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase()}
                          </span>
                          <span className="font-semibold text-foreground">
                            {u.name} {self && <span className="text-xs font-normal text-muted-foreground">(you)</span>}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        <p>{u.email}</p>
                        {u.username && <p className="text-xs">@{u.username}</p>}
                      </td>
                      <td className="px-4 py-3">
                        <select
                          aria-label={`Specialty for ${u.name}`}
                          data-testid="user-specialty-select"
                          className="h-8 rounded-[8px] border px-2 text-xs font-semibold"
                          style={{
                            borderColor: specColor(u.specialty) || "hsl(var(--border))",
                            color: specColor(u.specialty) || "hsl(var(--muted-foreground))",
                            backgroundColor: specColor(u.specialty) ? `${specColor(u.specialty)}14` : "transparent",
                          }}
                          value={u.specialty || ""}
                          onChange={(e) => setSpecialty.mutate({ user: u, specialty: e.target.value })}
                        >
                          <option value="">No specialty</option>
                          {Object.entries(specialties).map(([k, v]) => (
                            <option key={k} value={k}>{v.label}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={u.role} />
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={u.status} />
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{u.last_login ? fmtRelative(u.last_login) : "Never"}</td>
                      <td className="px-4 py-3 text-muted-foreground">{u.upcoming_appointments || 0}</td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1">
                          {u.status === "invited" && (
                            <Btn variant="ghost" size="icon" title="Resend invitation" onClick={() => action.mutate({ type: "resend", user: u })} data-testid="user-resend-invite-button">
                              <MailPlus />
                            </Btn>
                          )}
                          {!self && u.status !== "invited" && (
                            <Btn variant="ghost" size="icon" title={u.role === "admin" ? "Promote to supervisor" : "Demote to admin"} onClick={() => setConfirm({ type: "role", user: u, role: u.role === "admin" ? "supervisor" : "admin" })} data-testid="user-role-button">
                              {u.role === "admin" ? <ArrowUpToLine /> : <ArrowDownToLine />}
                            </Btn>
                          )}
                          {!self && u.status === "active" && (
                            <Btn variant="ghost" size="icon" title="Deactivate" onClick={() => setConfirm({ type: "deactivate", user: u })} data-testid="user-deactivate-button">
                              <PowerOff className="text-muted-foreground" />
                            </Btn>
                          )}
                          {!self && u.status === "inactive" && (
                            <Btn variant="ghost" size="icon" title="Reactivate" onClick={() => action.mutate({ type: "activate", user: u })} data-testid="user-activate-button">
                              <Power className="text-[hsl(var(--app-leaf))]" />
                            </Btn>
                          )}
                          {!self && (
                            <Btn variant="ghost" size="icon" title="Remove" onClick={() => setConfirm({ type: "remove", user: u })} data-testid="user-remove-button">
                              <Trash2 className="text-destructive" />
                            </Btn>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Invite dialog */}
      <Dialog
        open={inviteOpen}
        onOpenChange={(o) => {
          setInviteOpen(o);
          if (!o) setInviteResult(null);
        }}
      >
        <DialogContent className="max-w-md bg-white" data-testid="invite-user-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" /> Invite a team member
            </DialogTitle>
            <DialogDescription>They will receive an email with a link to set their own password. The link is valid for 7 days.</DialogDescription>
          </DialogHeader>
          {inviteResult ? (
            <div className="space-y-3" data-testid="invite-success">
              <p className="text-sm text-foreground">Invitation created. If email delivery is not configured yet, share this link directly:</p>
              <div className="flex gap-2">
                <input readOnly className="emerge-input !h-10 flex-1 text-xs" value={`${origin}${inviteResult.invite_link}`} data-testid="invite-link-input" />
                <Btn variant="secondary" onClick={() => copy(`${origin}${inviteResult.invite_link}`)} data-testid="invite-link-copy-button">
                  <Copy /> Copy
                </Btn>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Btn variant="secondary" onClick={() => setInviteResult(null)}>
                  Invite another
                </Btn>
                <Btn onClick={() => setInviteOpen(false)}>Done</Btn>
              </div>
            </div>
          ) : (
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                invite.mutate();
              }}
            >
              <div>
                <label htmlFor="inv-name" className="mb-1.5 block text-sm font-semibold">Full name</label>
                <input id="inv-name" className="emerge-input !h-10 text-sm" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required minLength={2} data-testid="invite-name-input" />
              </div>
              <div>
                <label htmlFor="inv-email" className="mb-1.5 block text-sm font-semibold">Email</label>
                <input id="inv-email" type="email" className="emerge-input !h-10 text-sm" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="invite-email-input" />
              </div>
              <LabeledSelect label="Role" id="inv-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="invite-role-select">
                <option value="admin">Admin - sees own appointments and the shared queue</option>
                <option value="supervisor">Supervisor - full access</option>
              </LabeledSelect>
              <div className="flex justify-end gap-2 pt-2">
                <Btn variant="secondary" onClick={() => setInviteOpen(false)}>
                  Cancel
                </Btn>
                <Btn type="submit" disabled={invite.isPending} data-testid="invite-submit-button">
                  {invite.isPending && <Loader2 className="animate-spin" />} Send invitation
                </Btn>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirm dialog */}
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="bg-white" data-testid="user-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm?.type === "remove" && `Remove ${confirm.user.name}?`}
              {confirm?.type === "deactivate" && `Deactivate ${confirm.user.name}?`}
              {confirm?.type === "role" && `${confirm.role === "supervisor" ? "Promote" : "Demote"} ${confirm.user.name}?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.type === "remove" && "Their account will be deleted. Submissions assigned to them become unassigned. Blocked if they still have upcoming appointments."}
              {confirm?.type === "deactivate" && "They will no longer be able to sign in. You can reactivate them later."}
              {confirm?.type === "role" && (confirm.role === "supervisor" ? "They will gain full access: all schedules, user management and the audit log." : "They will only see their own appointments and the shared queue.")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className={confirm?.type === "remove" ? "bg-destructive hover:bg-destructive/90" : ""} onClick={() => action.mutate(confirm)} data-testid="user-confirm-action-button">
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Blocked dialog */}
      <AlertDialog open={!!blocked} onOpenChange={(o) => !o && setBlocked(null)}>
        <AlertDialogContent className="bg-white" data-testid="user-blocked-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Reassign appointments first</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <p>{blocked?.message}</p>
                <ul className="mt-3 space-y-1 text-sm text-foreground">
                  {blocked?.appointments?.map((a) => (
                    <li key={a.id} className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2">
                      <span>
                        {fmtDateTime(a.start)} - {a.beneficiary_name}
                      </span>
                      <a href={`/dashboard/schedule?appointment=${a.id}`} className="text-xs font-semibold text-primary hover:underline">
                        Open
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction>Understood</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
